import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Grid,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from '../../api/axios';

const ScheduleManager = () => {
  const [schedules, setSchedules] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    specialistRole: '',
    schedule: {
      monday: '',
      tuesday: '',
      wednesday: '',  
      thursday: '',
      friday: '',
      saturday: 'Выходной',
      sunday: 'Выходной',
    },
    order: 0,
    isActive: true,
  });

  const dayLabels = {
    monday: 'Понедельник',
    tuesday: 'Вторник',
    wednesday: 'Среда',
    thursday: 'Четверг',
    friday: 'Пятница',
    saturday: 'Суббота',
    sunday: 'Воскресенье',
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/schedules', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSchedules(response.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setError('Ошибка загрузки расписания');
    }
  };

  const handleOpen = (schedule = null) => {
    if (schedule) {
      setEditingId(schedule._id);
      setFormData({
        specialistRole: schedule.specialistRole,
        schedule: schedule.schedule,
        order: schedule.order,
        isActive: schedule.isActive,
      });
    } else {
      setEditingId(null);
      setFormData({
        specialistRole: '',
        schedule: {
          monday: '',
          tuesday: '',
          wednesday: '',
          thursday: '',
          friday: '',
          saturday: 'Выходной',
          sunday: 'Выходной',
        },
        order: 0,
        isActive: true,
      });
    }
    setOpen(true);
    setError('');
    setSuccess('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
  };

  const handleScheduleChange = (day, value) => {
    setFormData({
      ...formData,
      schedule: {
        ...formData.schedule,
        [day]: value,
      },
    });
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      if (editingId) {
        await axios.put(`/schedules/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Расписание успешно обновлено');
      } else {
        await axios.post('/schedules', formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Расписание успешно создано');
      }
      handleClose();
      fetchSchedules();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving schedule:', error);
      setError('Ошибка при сохранении расписания');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить это расписание?')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/schedules/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Расписание успешно удалено');
      fetchSchedules();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting schedule:', error);
      setError('Ошибка при удалении расписания');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Управление расписанием
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Добавить расписание
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Должность</TableCell>
              <TableCell>Порядок</TableCell>
              <TableCell>Активно</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.map((schedule) => (
              <TableRow key={schedule._id}>
                <TableCell>{schedule.specialistRole}</TableCell>
                <TableCell>{schedule.order}</TableCell>
                <TableCell>
                  {schedule.isActive ? '✓' : '✗'}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    color="primary"
                    onClick={() => handleOpen(schedule)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(schedule._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? 'Редактировать расписание' : 'Добавить расписание'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Должность"
                  value={formData.specialistRole}
                  onChange={(e) =>
                    setFormData({ ...formData, specialistRole: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Порядок отображения"
                  value={formData.order}
                  onChange={(e) =>
                    setFormData({ ...formData, order: parseInt(e.target.value) })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                    />
                  }
                  label="Активно"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Расписание по дням
                </Typography>
              </Grid>

              {Object.keys(dayLabels).map((day) => (
                <Grid item xs={12} sm={6} key={day}>
                  <TextField
                    fullWidth
                    label={dayLabels[day]}
                    value={formData.schedule[day]}
                    onChange={(e) => handleScheduleChange(day, e.target.value)}
                    placeholder="например, 8:00-12:00"
                    helperText="Оставьте пустым или укажите 'Выходной'"
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.specialistRole}
          >
            {editingId ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduleManager;
